import api from '../utils/api.js';

/**
 * パフォーマンスダッシュボード (SS-04) API 呼び出し集約層
 *
 * 入出力は PBI-006_パフォーマンスダッシュボード集計API設計.md（以下「集計 API 設計」）§2・§4。
 * 5 本は別々に失敗しうるため、1 本ずつ結果を返す（SCR-P-001 §6.3。呼出側は部品ごとに表示を分ける）。
 * 失敗の形は matchingApi.js と同じく `{ok:false, status, errorKind}`。自動リトライはしない。
 */

/** API 5 本のパス（集計 API 設計 §4） */
export const DASHBOARD_ENDPOINTS = {
    KPI: 'kpi',
    DECISION_RATE: 'decision-rate',
    ASSIGNEE_ACTIVITY: 'assignee-activity',
    STAGE_TRANSITION: 'stage-transition',
    SPEECH_TOPIC: 'speech-topic',
};

/** 失敗の区分。表示の文言は画面側 (SCR-P-001 §6.3) */
export const DASHBOARD_ERROR_KINDS = {
    FORBIDDEN: 'forbidden', // 403（担当者が自分以外を指定した等。集計 API 設計 §2.6）
    FAILED: 'failed', // 通信の失敗・5xx・422・応答の形の誤り
};

const FORBIDDEN_STATUS = 403;

/**
 * クエリ文字列を作る（集計 API 設計 §2.1）。
 * assignee_ids は同じ名前を繰り返す形（`?assignee_ids=a&assignee_ids=b`）。
 * 期間の範囲（from・to。JST の日付 `YYYY-MM-DD`）は指定したときだけ付ける（SCR-P-001 IN-P-003。#661）
 *
 * @param {{period: string, assigneeIds?: string[], range?: {from: string, to: string}|null}} filters
 * @returns {string} 先頭の `?` を含まない
 */
export function buildDashboardQuery({ period, assigneeIds = [], range = null }) {
    const params = new URLSearchParams();
    params.append('period', period);
    if (range) {
        params.append('from', range.from);
        params.append('to', range.to);
    }
    assigneeIds.forEach((id) => params.append('assignee_ids', id));
    return params.toString();
}

/**
 * 応答の共通の形（集計 API 設計 §2.5）と、API ごとに画面が使う項目があるかを確かめる。
 * 形が違う応答を描画して画面を落とさないため、取得の失敗と同じに扱う
 */
const RESPONSE_CHECKS = {
    [DASHBOARD_ENDPOINTS.KPI]: (d) => isObject(d.current) && isObject(d.change),
    [DASHBOARD_ENDPOINTS.DECISION_RATE]: (d) => Array.isArray(d.periods),
    [DASHBOARD_ENDPOINTS.ASSIGNEE_ACTIVITY]: (d) => Array.isArray(d.assignees),
    [DASHBOARD_ENDPOINTS.STAGE_TRANSITION]: (d) => Array.isArray(d.stages) && Array.isArray(d.periods),
    [DASHBOARD_ENDPOINTS.SPEECH_TOPIC]: (d) => Array.isArray(d.periods) && Array.isArray(d.topics) && Array.isArray(d.assignees),
};

function isObject(v) {
    return v !== null && typeof v === 'object' && !Array.isArray(v);
}

export function isValidDashboardResponse(endpoint, data) {
    if (!isObject(data) || !isObject(data.scope)) return false;
    const check = RESPONSE_CHECKS[endpoint];
    return Boolean(check && check(data));
}

/**
 * ダッシュボードの API を 1 本呼ぶ。
 *
 * @param {string} endpoint - DASHBOARD_ENDPOINTS の値
 * @param {{period: string, assigneeIds?: string[], range?: {from: string, to: string}|null}} filters
 * @returns {Promise<{ok: true, data: object} | {ok: false, status: number, errorKind: string}>}
 */
export async function fetchDashboardData(endpoint, filters) {
    let response;
    try {
        response = await api.fetch(`/api/dashboard/${endpoint}?${buildDashboardQuery(filters)}`);
    } catch {
        // 通信の失敗。api.js は fetch の例外を再送出するため、ここで受けないと部品が読み込み中のまま止まる
        return { ok: false, status: 0, errorKind: DASHBOARD_ERROR_KINDS.FAILED };
    }

    // 401 のトークン更新に失敗した場合。api.js が /login へ移す
    if (!response) {
        return { ok: false, status: 0, errorKind: DASHBOARD_ERROR_KINDS.FAILED };
    }

    if (!response.ok) {
        const errorKind = response.status === FORBIDDEN_STATUS
            ? DASHBOARD_ERROR_KINDS.FORBIDDEN
            : DASHBOARD_ERROR_KINDS.FAILED;
        return { ok: false, status: response.status, errorKind };
    }

    const data = await response.json().catch(() => null);
    if (!isValidDashboardResponse(endpoint, data)) {
        return { ok: false, status: response.status, errorKind: DASHBOARD_ERROR_KINDS.FAILED };
    }
    return { ok: true, data };
}
